export async function getCroppedImage(file: File, uri: string, roomType?: string) {
    const image = await imageURIToImage(uri)
    const croppedCanvas = cropForVsai(image, roomType)
    const croppedFile = await canvasToFile(croppedCanvas, file)
    return croppedFile
}

function imageURIToImage(uri: string) {
    return new Promise((resolve) => {
        const image = new Image();
        image.src = uri
    
        image.onload = () => {
            resolve(image);
        };
    })
}

async function canvasToFile(canvas: HTMLCanvasElement, oldFile: File) {
    const blob = await canvasToBlob(canvas, oldFile.type)
    const file = new File([blob], oldFile.name, {type: blob.type});
    return file
}

function canvasToBlob(canvas: HTMLCanvasElement, imageType: string) {
    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            resolve(blob)
          }, imageType);
    })
}

/**
 * cropForVsai – 画像を VSAI 推奨アスペクト比にクロップ
 * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap} src
 * @param {string} roomType - 部屋タイプ（'oneRoom' の場合は特別な処理）
 * @returns {HTMLCanvasElement} クロップ済み Canvas
 */
export function cropForVsai(src, roomType?: string) {
    // oneRoom選択時は異なる比率を使用
    const ratios = roomType === 'oneRoom' ? [
      [3, 2],
      [37, 27],
      [1, 1],
      [7, 9],
      [17, 15],
      [35, 29]
    ] : [
      [1, 1],  // 12:12
      [12, 11],
      [6, 5],
      [4, 3],
      [3, 2],
      [12, 7],
      [2, 1]   // 12:6
    ];

    let bestLoss = Number.POSITIVE_INFINITY;
    /** @type {{ratio:[number,number], rotate:boolean, inverted:boolean}|null} */
    let bestCfg  = null;

    const oriW = src.width;
    const oriH = src.height;
  
    // ------------------------------------------------------------
    // 2) 最良の比率・回転・縦横入れ替えを探す
    // ------------------------------------------------------------
    let shouldBreak = false;
    for (const base of ratios) {
      for (const inv of [false, true]) {
        for (const rot of [false, true]) {
          const ratio = inv ? [base[1], base[0]] : base;      // (w,h)
          const [rw, rh] = ratio;

          // 回転後の寸法
          const rotW = rot ? oriH : oriW;
          const rotH = rot ? oriW : oriH;

          const newW   = Math.floor(rotW / rw) * rw;          // 整数倍幅
          if (newW === 0) continue;
          const scaledH = Math.round(rotH / rotW * newW);     // 縦横比固定で縮小
          const newH    = Math.floor(newW / rw * rh);         // 目標高

          if (scaledH < newH) continue;                       // 足りないと NG
          const loss = (scaledH - newH) / scaledH;            // 余剰の割合

          if (loss < bestLoss) {
            bestLoss = loss;
            bestCfg  = { ratio, rotate: rot, inverted: inv };
            if (loss < 0.01) {
              shouldBreak = true;
              break;
            }
          }
        }
        if (shouldBreak) break;
      }
      if (shouldBreak) break;
    }
    if (!bestCfg) throw new Error("適切な比率が見つかりません");
  
    // ------------------------------------------------------------
    // 3) Canvas に描画してリサイズ → 上下トリミング
    // ------------------------------------------------------------
    const { ratio: [rw, rh], rotate } = bestCfg;
  
    // 3-1) 回転が必要なら 90° 回しておく
    const tmp = document.createElement('canvas');
    if (rotate) {
      tmp.width  = oriH;
      tmp.height = oriW;
      const tctx = tmp.getContext('2d');
      tctx.translate(tmp.width / 2, tmp.height / 2);
      tctx.rotate(Math.PI / 2);
      tctx.drawImage(src, -oriW / 2, -oriH / 2);
    } else {
      tmp.width  = oriW;
      tmp.height = oriH;
      tmp.getContext('2d').drawImage(src, 0, 0);
    }
  
    // 3-2) 幅を rw の倍数に縮小
    const rotW = tmp.width;
    const rotH = tmp.height;
    const newW   = Math.floor(rotW / rw) * rw;
    const scaledH = Math.round(rotH / rotW * newW);
    const newH    = Math.floor(newW / rw * rh);
    const cropTop = Math.floor((scaledH - newH) / 2); // 上下センタリング
  
    const resized = document.createElement('canvas');
    resized.width  = newW;
    resized.height = scaledH;
    resized.getContext('2d').drawImage(tmp, 0, 0, newW, scaledH);
  
    const cropped = document.createElement('canvas');
    cropped.width  = newW;
    cropped.height = newH;
    cropped
      .getContext('2d')
      .drawImage(resized, 0, cropTop, newW, newH, 0, 0, newW, newH);
  
    // 3-3) 元が回転していた場合は -90° で戻す
    if (rotate) {
      const finalCnv = document.createElement('canvas');
      finalCnv.width  = newH;
      finalCnv.height = newW;
      const fctx = finalCnv.getContext('2d');
      fctx.translate(finalCnv.width / 2, finalCnv.height / 2);
      fctx.rotate(-Math.PI / 2);
      fctx.drawImage(cropped, -cropped.width / 2, -cropped.height / 2);
      return finalCnv;
    }
    return cropped;
  }